import React from 'react';

import { Chip } from './chip';
import { Icon } from './icon';
import { Button } from './shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from './shadcn/tooltip';
import { FilterPill } from '@/utils/buildFilterPills';

interface FilterPillsProps {
  pills: FilterPill[];
  onClearAll?: () => void;
}

const FilterPillComponent = ({ pill, className }: { pill: FilterPill; className?: string }) => {
  return (
    <Chip key={pill.id} className={`border ${pill.color} ${className}`}>
      {pill.label}
      {pill.onRemove && (
        <button
          onClick={pill.onRemove}
          className="rounded-md"
          title="Remove filter"
          aria-label={`Remove ${pill.label} filter`}
        >
          <Icon className="lucide--x" />
        </button>
      )}
    </Chip>
  );
};

export const FilterPills: React.FC<FilterPillsProps> = ({ pills, onClearAll }) => {
  if (pills.length === 0) {
    return null;
  }

  const renderPill = (pill: FilterPill) => {
    if (pill.nonVisibleItems && pill.nonVisibleItems.length > 0) {
      return (
        <Tooltip key={pill.id} delayDuration={100}>
          <TooltipTrigger>
            <FilterPillComponent pill={pill} className="border-none bg-transparent" />
          </TooltipTrigger>
          <TooltipContent sideOffset={6} className="w-64 p-1" side="bottom" align="start">
            <div className="max-h-59 space-y-1 overflow-y-auto">
              {pill.nonVisibleItems?.map((item, index) => (
                <div
                  key={index}
                  className="truncate rounded px-2 py-1 text-xs text-gray-700 capitalize hover:bg-gray-50"
                >
                  {item}
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      );
    }

    return <FilterPillComponent pill={pill} />;
  };

  return (
    <div className="mt-2 flex items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-1.5">{pills.map(renderPill)}</div>
      {onClearAll && (
        <Button onClick={onClearAll} size="sm" aria-label="Clear all filters">
          <Icon className="lucide--funnel-x" />
          Clear all
        </Button>
      )}
    </div>
  );
};
